/**
 * Чистая логика фильтров каталога, вынесенная из CatalogView.
 *
 * Здесь нет Vue-реактивности и HTTP: только предикаты и форматирование,
 * которые можно покрыть unit-тестами без монтирования компонента.
 */

export type DynamicAttributeFilter = {
  id: number
  name: string
  type: 'string' | 'list' | 'number' | 'boolean'
  unit?: string | null
  options?: Array<string | { value: boolean; label: string }>
  range?: {
    min: number | null
    max: number | null
  }
}

export type AttributeValueFilters = Record<string, string[]>
export type AttributeRangeFilters = Record<string, { min: string; max: string }>
export type AttributeBooleanFilters = Record<string, string>

export type PriceBounds = { min: number; max: number }

export type ActiveFilterChip =
  | { key: string; type: 'search'; label: string }
  | { key: string; type: 'category'; label: string }
  | { key: string; type: 'manufacturer'; label: string; manufacturerId: string }
  | { key: string; type: 'supplier'; label: string }
  | { key: string; type: 'in_stock'; label: string }
  | { key: string; type: 'price_range'; label: string }
  | { key: string; type: 'attribute_value'; label: string; attributeId: string; value: string }
  | { key: string; type: 'attribute_range'; label: string; attributeId: string }
  | { key: string; type: 'attribute_bool'; label: string; attributeId: string }

/** Состояние фильтров, из которого строятся чипы и параметры запроса. */
export type CatalogFilterState = {
  search: string
  selectedCategory: string | null
  selectedSupplier: string | null
  manufacturers: readonly string[]
  priceRange: readonly [number, number]
  inStock: boolean
  attributeValues: AttributeValueFilters
  attributeRanges: AttributeRangeFilters
  attributeBooleans: AttributeBooleanFilters
}

/** Резолверы человекочитаемых имён - каталог знает их из загруженных справочников. */
export type CatalogFilterLabels = {
  categoryName: (categoryId: string) => string
  manufacturerName: (manufacturerId: string) => string
  supplierName: (supplierId: string) => string
  attributes: readonly DynamicAttributeFilter[]
}

/** Пустая строка в select'е означает «не выбрано». */
export function normalizeSelectValue(value: string | null): string | null {
  if (value === null || value === '') {
    return null
  }

  return value
}

export { pluralize } from '@/shared/lib/plural'

/** Выбрано ли хоть одно значение среди динамических характеристик. */
export function hasAttributeSelections(
  valueFilters: AttributeValueFilters,
  rangeFilters: AttributeRangeFilters,
  booleanFilters: AttributeBooleanFilters,
): boolean {
  if (Object.values(booleanFilters).some((value) => value === 'true' || value === 'false')) {
    return true
  }

  if (Object.values(valueFilters).some((values) => values.length > 0)) {
    return true
  }

  return Object.values(rangeFilters).some(
    (range) => range.min.trim() !== '' || range.max.trim() !== '',
  )
}

/** Сужен ли диапазон цены относительно доступных границ. */
export function isPriceRangeNarrowed(
  priceRange: readonly [number, number],
  bounds: PriceBounds,
): boolean {
  return priceRange[0] > bounds.min || priceRange[1] < bounds.max
}

/** Активны ли «расширенные» фильтры (производители/цена). */
export function hasAdvancedFilters(
  manufacturers: readonly string[],
  priceRange: readonly [number, number],
  bounds: PriceBounds,
): boolean {
  return manufacturers.length > 0 || isPriceRangeNarrowed(priceRange, bounds)
}

/**
 * Счётчик активных расширенных фильтров: каждый производитель считается
 * отдельно, суженный диапазон цены - как один фильтр.
 */
export function countActiveFilters(
  manufacturers: readonly string[],
  priceRange: readonly [number, number],
  bounds: PriceBounds,
): number {
  let count = 0

  if (manufacturers.length > 0) {
    count += manufacturers.length
  }

  if (isPriceRangeNarrowed(priceRange, bounds)) {
    count += 1
  }

  return count
}

/**
 * Подписи вариантов характеристики: строковые опции как есть, объектные - по label.
 * Пустые подписи отбрасываются, иначе в фильтрах появляются безымянные чекбоксы.
 */
export function attributeOptionLabels(attribute: DynamicAttributeFilter): string[] {
  return (attribute.options ?? [])
    .map((option) => (typeof option === 'string' ? option : option.label))
    .filter((option) => option.trim() !== '')
}

export function findAttributeById(
  attributes: readonly DynamicAttributeFilter[],
  attributeId: string,
): DynamicAttributeFilter | undefined {
  return attributes.find((attribute) => String(attribute.id) === attributeId)
}

export function getAttributeName(
  attributes: readonly DynamicAttributeFilter[],
  attributeId: string,
): string {
  return findAttributeById(attributes, attributeId)?.name ?? `Характеристика #${attributeId}`
}

/** Подпись чипа для диапазонной характеристики: «Вес: 10-20 кг», «Вес: от 10 кг». */
export function formatAttributeRangeLabel(
  attributes: readonly DynamicAttributeFilter[],
  attributeId: string,
  min: string,
  max: string,
): string {
  const attribute = findAttributeById(attributes, attributeId)
  const unit = attribute?.unit ? ` ${attribute.unit}` : ''
  const name = getAttributeName(attributes, attributeId)

  if (min && max) return `${name}: ${min}-${max}${unit}`
  if (min) return `${name}: от ${min}${unit}`
  return `${name}: до ${max}${unit}`
}

/**
 * Чипы активных фильтров в порядке отображения: поиск, категория, цена,
 * производители, поставщик, наличие, затем динамические характеристики.
 */
export function buildActiveFilterChips(
  state: CatalogFilterState,
  bounds: PriceBounds,
  labels: CatalogFilterLabels,
): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = []
  const selectedCategoryId = normalizeSelectValue(state.selectedCategory)
  const selectedSupplierId = normalizeSelectValue(state.selectedSupplier)
  const searchTerm = state.search.trim()

  if (searchTerm) {
    chips.push({ key: `search:${searchTerm}`, type: 'search', label: `Поиск: ${searchTerm}` })
  }

  if (selectedCategoryId) {
    chips.push({
      key: `category:${selectedCategoryId}`,
      type: 'category',
      label: `Категория: ${labels.categoryName(selectedCategoryId)}`,
    })
  }

  if (isPriceRangeNarrowed(state.priceRange, bounds)) {
    chips.push({
      key: `price:${state.priceRange[0]}-${state.priceRange[1]}`,
      type: 'price_range',
      label: `Цена: ${state.priceRange[0]}-${state.priceRange[1]} ₽`,
    })
  }

  for (const manufacturerId of state.manufacturers) {
    chips.push({
      key: `manufacturer:${manufacturerId}`,
      type: 'manufacturer',
      label: `Производитель: ${labels.manufacturerName(manufacturerId)}`,
      manufacturerId,
    })
  }

  if (selectedSupplierId) {
    chips.push({
      key: `supplier:${selectedSupplierId}`,
      type: 'supplier',
      label: `Поставщик: ${labels.supplierName(selectedSupplierId)}`,
    })
  }

  if (state.inStock) {
    chips.push({ key: 'in_stock', type: 'in_stock', label: 'В наличии' })
  }

  for (const [attributeId, values] of Object.entries(state.attributeValues)) {
    for (const value of values) {
      chips.push({
        key: `attr-value:${attributeId}:${value}`,
        type: 'attribute_value',
        label: `${getAttributeName(labels.attributes, attributeId)}: ${value}`,
        attributeId,
        value,
      })
    }
  }

  for (const [attributeId, range] of Object.entries(state.attributeRanges)) {
    const min = range.min.trim()
    const max = range.max.trim()
    if (!min && !max) {
      continue
    }

    chips.push({
      key: `attr-range:${attributeId}:${min}:${max}`,
      type: 'attribute_range',
      label: formatAttributeRangeLabel(labels.attributes, attributeId, min, max),
      attributeId,
    })
  }

  for (const [attributeId, value] of Object.entries(state.attributeBooleans)) {
    if (value !== 'true' && value !== 'false') {
      continue
    }

    chips.push({
      key: `attr-bool:${attributeId}:${value}`,
      type: 'attribute_bool',
      label: `${getAttributeName(labels.attributes, attributeId)}: ${value === 'true' ? 'Да' : 'Нет'}`,
      attributeId,
    })
  }

  return chips
}

/**
 * Параметры запроса по динамическим характеристикам:
 * attr_{id} - список значений, attr_{id}_min/_max - диапазон, attr_{id}_bool - флаг.
 */
export function buildAttributeFilterParams(
  attributeValues: AttributeValueFilters,
  attributeRanges: AttributeRangeFilters,
  attributeBooleans: AttributeBooleanFilters,
): Record<string, string | string[]> {
  const params: Record<string, string | string[]> = {}

  for (const [attributeId, values] of Object.entries(attributeValues)) {
    if (values.length > 0) {
      params[`attr_${attributeId}`] = values
    }
  }

  for (const [attributeId, range] of Object.entries(attributeRanges)) {
    const min = range.min.trim()
    const max = range.max.trim()

    if (min !== '') {
      params[`attr_${attributeId}_min`] = min
    }
    if (max !== '') {
      params[`attr_${attributeId}_max`] = max
    }
  }

  for (const [attributeId, value] of Object.entries(attributeBooleans)) {
    if (value === 'true' || value === 'false') {
      params[`attr_${attributeId}_bool`] = value
    }
  }

  return params
}
